import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { ConfirmDialog } from '@shared/components/confirmDialog/ConfirmDialog';
import { CustomSuccessToast } from '@shared/components/customSuccessToast';
import BlankCard from '@shared/components/shared/BlankCard';
import Spinner from '@shared/components/spinner/Spinner';
import { useErrorHandler } from '@shared/hooks/useErrorHandler';
import {
  useCampaignStructuredOutputGenerateControllerGenerateMutation,
  useCampaignStructuredOutputAttachControllerAttachMutation,
  useCampaignStructuredOutputDetachControllerDetachMutation,
  useCampaignStructuredOutputListControllerListQuery,
  useStructuredOutputCreateControllerCreateMutation,
  useStructuredOutputUpdateControllerUpdateMutation,
} from '@shared/services/apiService/apiService';
import {
  createDefaultRootSchemaNode,
  parseStructuredOutputSchema,
  StructuredOutputFormFields,
  serializeStructuredOutputSchema,
  useStructuredOutputSchemaDraft,
  validateStructuredOutputSchemaEditorValue,
} from '@shared/components/StructuredOutputEditor';
import { getPrimaryCampaignStructuredOutput } from '@features/Admin/Campaign/utils/call-analysis-structured-output';

type CallAnalysisStructuredOutputSectionProps = {
  campaignId: string;
  isDisabled: boolean;
  isSquadCampaign?: boolean;
  onDirtyChange?: (isDirty: boolean) => void;
  onActionsChange?: (actions: CallAnalysisActions | null) => void;
};

export type CallAnalysisActions = {
  save: () => Promise<boolean>;
  discard: () => void;
  isBusy: boolean;
};

type DraftMode = 'empty' | 'draft' | 'existing';

type DraftOrigin = 'generated' | 'manual';

type StructuredOutputEditorSnapshot = {
  name: string;
  description: string;
  instructions: string | null;
  schema: string;
};

const normalizeTextValue = (value: string | null | undefined) => value?.trim() ?? '';

const normalizeInstructionsValue = (value: string | null | undefined) => {
  const normalizedValue = value?.trim() ?? '';

  return normalizedValue ? normalizedValue : null;
};

const normalizeSchemaString = (schema: string) => {
  const parsedSchema = parseStructuredOutputSchema(schema);

  if (!parsedSchema.ok) {
    return schema.trim();
  }

  return JSON.stringify(serializeStructuredOutputSchema(parsedSchema.value));
};

const buildSnapshotFromValues = ({
  name,
  description,
  instructions,
  schema,
}: {
  name: string;
  description: string;
  instructions: string | null | undefined;
  schema: string;
}): StructuredOutputEditorSnapshot => ({
  name: normalizeTextValue(name),
  description: normalizeTextValue(description),
  instructions: normalizeInstructionsValue(instructions),
  schema: normalizeSchemaString(schema),
});

const CallAnalysisStructuredOutputSection: React.FC<CallAnalysisStructuredOutputSectionProps> = ({
  campaignId,
  isDisabled,
  isSquadCampaign = false,
  onDirtyChange,
  onActionsChange,
}) => {
  const { t } = useTranslation();
  const { showError } = useErrorHandler();
  const [createStructuredOutput] = useStructuredOutputCreateControllerCreateMutation();
  const [updateStructuredOutput] = useStructuredOutputUpdateControllerUpdateMutation();
  const [attachStructuredOutput] = useCampaignStructuredOutputAttachControllerAttachMutation();
  const [detachStructuredOutput] = useCampaignStructuredOutputDetachControllerDetachMutation();
  const [generateStructuredOutput] =
    useCampaignStructuredOutputGenerateControllerGenerateMutation();
  const {
    data: structuredOutputs = [],
    currentData: currentStructuredOutputs,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useCampaignStructuredOutputListControllerListQuery(
    { id: campaignId },
    {
      skip: !campaignId,
      refetchOnMountOrArgChange: true,
    },
  );

  const {
    schemaNode,
    rawJson,
    schemaError,
    mode,
    isJsonLocked,
    canRecoverJson,
    canSwitchToVisual,
    reset,
    initializeFromSchemaString,
    initializeWithNode,
    handleVisualChange,
    handleJsonChange,
    openJsonMode,
    openVisualMode,
    revertToLastValidJson,
    formatJson,
  } = useStructuredOutputSchemaDraft();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [instructions, setInstructions] = useState('');
  const [draftMode, setDraftMode] = useState<DraftMode>('empty');
  const [draftOrigin, setDraftOrigin] = useState<DraftOrigin | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isDetaching, setIsDetaching] = useState(false);
  const [detachCandidate, setDetachCandidate] = useState<{
    campaignId: string;
    outputId: string;
  } | null>(null);
  const [detached, setDetached] = useState<{ campaignId: string; outputId: string } | null>(null);
  const detachBusy = useRef(false);
  const detachEpoch = useRef(0);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  useEffect(() => {
    detachEpoch.current += 1;
    detachBusy.current = false;
    setIsDetaching(false);
    setDetachCandidate(null);
    setDetached(null);
  }, [campaignId]);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [isNameTouched, setIsNameTouched] = useState(false);
  const [isDescriptionTouched, setIsDescriptionTouched] = useState(false);
  const [isSchemaTouched, setIsSchemaTouched] = useState(false);
  const [showRegenerateConfirmDialog, setShowRegenerateConfirmDialog] = useState(false);
  const [baselineSnapshot, setBaselineSnapshot] = useState<StructuredOutputEditorSnapshot | null>(
    null,
  );

  const { primaryStructuredOutput, hasMultipleStructuredOutputs } = useMemo(
    () =>
      getPrimaryCampaignStructuredOutput(
        structuredOutputs.filter(
          (row) => !(detached?.campaignId === campaignId && detached.outputId === row.id),
        ),
      ),
    [structuredOutputs, detached, campaignId],
  );

  const resetValidationState = useCallback(() => {
    setHasSubmitted(false);
    setIsNameTouched(false);
    setIsDescriptionTouched(false);
    setIsSchemaTouched(false);
  }, []);

  const clearDraft = useCallback(() => {
    setName('');
    setDescription('');
    setInstructions('');
    setDraftMode('empty');
    setDraftOrigin(null);
    setBaselineSnapshot(null);
    reset();
    resetValidationState();
  }, [reset, resetValidationState]);

  const hydrateFromStructuredOutput = useCallback(
    (nextStructuredOutput: NonNullable<typeof primaryStructuredOutput>) => {
      setName(nextStructuredOutput.name ?? '');
      setDescription(nextStructuredOutput.description ?? '');
      setInstructions(nextStructuredOutput.instructions ?? '');
      setDraftMode('existing');
      setDraftOrigin(null);
      setBaselineSnapshot(
        buildSnapshotFromValues({
          name: nextStructuredOutput.name ?? '',
          description: nextStructuredOutput.description ?? '',
          instructions: nextStructuredOutput.instructions ?? null,
          schema: nextStructuredOutput.schema,
        }),
      );
      initializeFromSchemaString(nextStructuredOutput.schema);
      resetValidationState();
    },
    [initializeFromSchemaString, resetValidationState],
  );

  const hasEditorDraft = draftMode !== 'empty' || Boolean(rawJson) || Boolean(schemaNode);

  const currentSnapshot = useMemo(() => {
    if (!hasEditorDraft) {
      return null;
    }

    const normalizedSchema = schemaError
      ? rawJson
      : schemaNode !== null
        ? JSON.stringify(serializeStructuredOutputSchema(schemaNode))
        : normalizeSchemaString(rawJson);

    return {
      name: normalizeTextValue(name),
      description: normalizeTextValue(description),
      instructions: normalizeInstructionsValue(instructions),
      schema: normalizedSchema,
    } satisfies StructuredOutputEditorSnapshot;
  }, [description, hasEditorDraft, instructions, name, rawJson, schemaNode, schemaError]);

  const isDirty = useMemo(() => {
    if (!currentSnapshot) {
      return false;
    }

    if (!baselineSnapshot) {
      return true;
    }

    return JSON.stringify(currentSnapshot) !== JSON.stringify(baselineSnapshot);
  }, [baselineSnapshot, currentSnapshot]);

  useEffect(() => {
    if (isDirty) {
      return;
    }

    if (primaryStructuredOutput) {
      hydrateFromStructuredOutput(primaryStructuredOutput);
      return;
    }

    if (draftMode !== 'draft') {
      clearDraft();
    }
  }, [clearDraft, draftMode, hydrateFromStructuredOutput, isDirty, primaryStructuredOutput]);

  const validationError = useMemo(() => {
    if (!hasEditorDraft) {
      return null;
    }

    if (!schemaNode) {
      return t('campaignPage.callAnalysis.editor.schemaJsonInvalid');
    }

    if (!name.trim()) {
      return t('campaignPage.callAnalysis.validation.nameRequired');
    }

    if (!description.trim()) {
      return t('campaignPage.callAnalysis.validation.descriptionRequired');
    }

    if (schemaError) {
      return t('campaignPage.callAnalysis.editor.schemaJsonInvalid');
    }

    return validateStructuredOutputSchemaEditorValue(schemaNode);
  }, [description, hasEditorDraft, name, schemaError, schemaNode, t]);

  const handleGenerate = useCallback(async () => {
    try {
      setIsGenerating(true);
      const generatedConfig = await generateStructuredOutput({
        id: campaignId,
      }).unwrap();

      setName(generatedConfig.name);
      setDescription(generatedConfig.description);
      setInstructions(generatedConfig.instructions);
      initializeFromSchemaString(generatedConfig.schema);
      setDraftMode('draft');
      setDraftOrigin('generated');
      setBaselineSnapshot(null);
      resetValidationState();
    } catch (error) {
      showError({
        title: t('campaignPage.callAnalysis.messages.generateErrorTitle'),
        error,
        suggestion: t('campaignPage.callAnalysis.messages.generateErrorSuggestion'),
      });
    } finally {
      setIsGenerating(false);
    }
  }, [
    campaignId,
    generateStructuredOutput,
    initializeFromSchemaString,
    resetValidationState,
    showError,
    t,
  ]);

  const handleCreateManually = useCallback(() => {
    setName('');
    setDescription('');
    setInstructions('');
    initializeWithNode(createDefaultRootSchemaNode());
    setDraftMode('draft');
    setDraftOrigin('manual');
    setBaselineSnapshot(null);
    resetValidationState();
  }, [initializeWithNode, resetValidationState]);

  const handleRegenerate = () => {
    if (isDisabled || isBusy) {
      return;
    }

    if (isDirty) {
      setShowRegenerateConfirmDialog(true);
      return;
    }

    void handleGenerate();
  };

  const handleRegenerateConfirm = async () => {
    setShowRegenerateConfirmDialog(false);
    await handleGenerate();
  };

  const handleCancel = useCallback(() => {
    if (isSaving || isGenerating || isDetaching || detachBusy.current) {
      return;
    }

    if (primaryStructuredOutput) {
      hydrateFromStructuredOutput(primaryStructuredOutput);
      return;
    }

    clearDraft();
  }, [
    clearDraft,
    hydrateFromStructuredOutput,
    isGenerating,
    isSaving,
    isDetaching,
    primaryStructuredOutput,
  ]);

  const handleSave = useCallback(async (): Promise<boolean> => {
    if (isDisabled || isSaving || isGenerating || detachBusy.current) return false;
    setHasSubmitted(true);

    if (!schemaNode || validationError) {
      return false;
    }

    try {
      setIsSaving(true);

      const payload = {
        name: name.trim(),
        description: description.trim(),
        instructions: instructions.trim() ? instructions.trim() : null,
        schema: serializeStructuredOutputSchema(schemaNode),
      };

      if (primaryStructuredOutput?.id) {
        await updateStructuredOutput({
          id: primaryStructuredOutput.id,
          structuredOutputUpdateRequestDto: payload,
        }).unwrap();
      } else {
        const createdStructuredOutput = await createStructuredOutput({
          structuredOutputCreateRequestDto: payload,
        }).unwrap();

        await attachStructuredOutput({
          id: campaignId,
          campaignStructuredOutputAttachRequestDto: {
            structuredOutputId: createdStructuredOutput.id,
          },
        }).unwrap();
      }

      toast.success(CustomSuccessToast, {
        closeButton: false,
        autoClose: 5000,
        icon: false,
        data: {
          title: t('common.saved'),
          description: t('campaignPage.callAnalysis.messages.saved'),
        },
      });

      setBaselineSnapshot(
        buildSnapshotFromValues({
          name: payload.name,
          description: payload.description,
          instructions: payload.instructions,
          schema: JSON.stringify(payload.schema),
        }),
      );
      setDraftMode('existing');
      await refetch();
      return true;
    } catch (error) {
      showError({
        title: t('campaignPage.callAnalysis.messages.saveErrorTitle'),
        error,
        suggestion: t('campaignPage.callAnalysis.messages.saveErrorSuggestion'),
      });
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [
    attachStructuredOutput,
    campaignId,
    createStructuredOutput,
    isDisabled,
    isSaving,
    isGenerating,
    description,
    instructions,
    name,
    primaryStructuredOutput?.id,
    refetch,
    schemaNode,
    showError,
    t,
    updateStructuredOutput,
    validationError,
  ]);

  const isBusy = isSaving || isGenerating || isDetaching;
  const canDetach =
    !isDisabled &&
    !isBusy &&
    !isFetching &&
    !isError &&
    !isDirty &&
    !!primaryStructuredOutput?.id &&
    !!currentStructuredOutputs?.some((row) => row.id === primaryStructuredOutput.id);
  const detachAccess = useRef({ campaignId, outputId: primaryStructuredOutput?.id, canDetach });
  detachAccess.current = { campaignId, outputId: primaryStructuredOutput?.id, canDetach };
  const handleDetach = async () => {
    const candidate = detachCandidate,
      access = detachAccess.current;
    if (
      !candidate ||
      detachBusy.current ||
      !access.canDetach ||
      access.campaignId !== candidate.campaignId ||
      access.outputId !== candidate.outputId
    ) {
      setDetachCandidate(null);
      return;
    }
    const operation = detachEpoch.current;
    detachBusy.current = true;
    setIsDetaching(true);
    setDetachCandidate(null);
    const stillCurrent = () =>
      mounted.current &&
      operation === detachEpoch.current &&
      detachAccess.current.campaignId === candidate.campaignId &&
      detachAccess.current.outputId === candidate.outputId;
    try {
      const result = await detachStructuredOutput({
        id: candidate.campaignId,
        outputId: candidate.outputId,
      }).unwrap();
      if (!result.ok) throw new Error('The analysis attachment was not detached.');
      if (!stillCurrent()) return;
      setDetached(candidate);
      clearDraft();
      await refetch();
    } catch (error) {
      if (stillCurrent())
        showError({
          title: 'Unable to confirm analysis detachment',
          error,
          suggestion:
            'Reload the current campaign attachments before retrying. The shared analysis template is not deleted.',
        });
    } finally {
      if (
        operation === detachEpoch.current &&
        detachAccess.current.campaignId === candidate.campaignId
      ) {
        detachBusy.current = false;
        if (mounted.current) setIsDetaching(false);
      }
    }
  };

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  useEffect(() => {
    onActionsChange?.({ save: handleSave, discard: handleCancel, isBusy });
    return () => onActionsChange?.(null);
  }, [handleCancel, handleSave, isBusy, onActionsChange]);
  const callAnalysisHelperText = t(
    isSquadCampaign
      ? 'campaignPage.callAnalysis.squadEditorHelperText'
      : 'campaignPage.callAnalysis.editorHelper',
  );
  const callAnalysisEmptyDescription = t(
    isSquadCampaign
      ? 'campaignPage.callAnalysis.squadHelperText'
      : 'campaignPage.callAnalysis.emptyDescription',
  );

  const detachmentNotice =
    detached?.campaignId === campaignId ? (
      <Alert severity="info">
        Analysis configuration detached from this campaign. The shared template and existing call
        results are preserved. Other attached schemas or a legacy analysis plan may still apply.
      </Alert>
    ) : null;

  if (isLoading) {
    return <Spinner />;
  }

  if (isError) {
    return (
      <BlankCard>
        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {detachmentNotice}
          <Typography variant="h6">{t('campaignPage.callAnalysis.errorTitle')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('common.errorMessage')}
          </Typography>
          <Box>
            <Button variant="contained" onClick={() => refetch()}>
              {t('common.retry')}
            </Button>
          </Box>
        </Box>
      </BlankCard>
    );
  }

  if (!hasEditorDraft) {
    return (
      <BlankCard>
        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {detachmentNotice}
          <Typography variant="h6">{t('campaignPage.callAnalysis.emptyTitle')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {callAnalysisEmptyDescription}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="flex-start">
            <Button
              variant="contained"
              onClick={handleGenerate}
              disabled={isDisabled || isGenerating || isFetching}>
              {isGenerating ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                t('campaignPage.callAnalysis.generateButton')
              )}
            </Button>
            <Button
              variant="outlined"
              onClick={handleCreateManually}
              disabled={isDisabled || isGenerating || isFetching}>
              {t('campaignPage.callAnalysis.createManuallyButton')}
            </Button>
          </Stack>
        </Box>
      </BlankCard>
    );
  }

  return (
    <Stack spacing={2}>
      {detachmentNotice}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
        gap={2}>
        <Box>
          {isFetching ? (
            <Typography variant="caption" color="text.secondary">
              {t('campaignPage.callAnalysis.syncing')}
            </Typography>
          ) : null}
        </Box>
        <Stack direction="row" spacing={1} flexShrink={0} justifyContent="flex-end">
          {primaryStructuredOutput?.id && (
            <Button
              color="warning"
              variant="outlined"
              disabled={!canDetach}
              onClick={() => {
                if (!detachAccess.current.canDetach || detachBusy.current) return;
                setDetachCandidate({ campaignId, outputId: primaryStructuredOutput.id });
              }}>
              Detach analysis from campaign
            </Button>
          )}
          <Button variant="outlined" onClick={handleRegenerate} disabled={isDisabled || isBusy}>
            {isGenerating ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              t('campaignPage.callAnalysis.regenerateButton')
            )}
          </Button>
          <Button variant="outlined" onClick={handleCancel} disabled={!isDirty || isBusy}>
            {t('action.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isDisabled || isBusy || !isDirty || Boolean(validationError)}>
            {isSaving ? <CircularProgress size={20} color="inherit" /> : t('action.save')}
          </Button>
        </Stack>
      </Stack>

      {primaryStructuredOutput?.id && isDirty && (
        <Alert severity="info">
          Save or discard unsaved metric edits before detaching the analysis configuration.
        </Alert>
      )}
      {hasMultipleStructuredOutputs ? (
        <Alert severity="warning">{t('campaignPage.callAnalysis.multipleOutputsWarning')}</Alert>
      ) : null}

      {draftMode === 'draft' && draftOrigin === 'generated' ? (
        <Alert severity="info">{t('campaignPage.callAnalysis.generatedNotice')}</Alert>
      ) : null}

      {draftMode === 'draft' && draftOrigin === 'manual' ? (
        <Alert severity="info">{t('campaignPage.callAnalysis.manualNotice')}</Alert>
      ) : null}

      <StructuredOutputFormFields
        name={name}
        description={description}
        instructions={instructions}
        schema={schemaNode}
        schemaError={schemaError}
        schemaMode={mode}
        rawSchemaJson={rawJson}
        canRecoverSchemaJson={canRecoverJson}
        canSwitchToVisualSchema={canSwitchToVisual}
        isSchemaJsonLocked={isJsonLocked}
        isSubmitting={isBusy || isDisabled}
        topHelperText={callAnalysisHelperText}
        showNameValidation={isNameTouched || hasSubmitted}
        showDescriptionValidation={isDescriptionTouched || hasSubmitted}
        showSchemaValidation={isSchemaTouched || hasSubmitted}
        onNameChange={(value) => {
          setName(value);
          setIsNameTouched(true);
        }}
        onDescriptionChange={(value) => {
          setDescription(value);
          setIsDescriptionTouched(true);
        }}
        onInstructionsChange={setInstructions}
        onSchemaChange={(value) => {
          handleVisualChange(value);
          setIsSchemaTouched(true);
        }}
        onSchemaJsonChange={(value) => {
          handleJsonChange(value);
          setIsSchemaTouched(true);
        }}
        onOpenSchemaJson={openJsonMode}
        onBackToVisualSchema={openVisualMode}
        onFormatSchemaJson={formatJson}
        onRevertSchemaJson={revertToLastValidJson}
        onNameBlur={() => setIsNameTouched(true)}
        onDescriptionBlur={() => setIsDescriptionTouched(true)}
      />

      <ConfirmDialog
        showDialogFlag={!!detachCandidate}
        handleClose={() => setDetachCandidate(null)}
        dialogTitle="Detach analysis from this campaign?"
        dialogContentText="This removes only the selected campaign attachment. The shared analysis template and existing call results are preserved. Other attached schemas or the campaign's legacy analysis plan may still apply."
        showDialog={(open) => {
          if (!open) setDetachCandidate(null);
        }}
        confirmDialog={handleDetach}
        variant="warning"
      />
      <ConfirmDialog
        showDialogFlag={showRegenerateConfirmDialog}
        handleClose={() => setShowRegenerateConfirmDialog(false)}
        dialogTitle="campaignPage.callAnalysis.regenerateConfirmTitle"
        dialogContentText={t('campaignPage.callAnalysis.regenerateConfirmDescription')}
        showDialog={setShowRegenerateConfirmDialog}
        confirmDialog={handleRegenerateConfirm}
      />
    </Stack>
  );
};

export default CallAnalysisStructuredOutputSection;
